import { useQuery } from '@tanstack/react-query';
import { Link, Text } from '../ui';
import { api } from '../lib/api';
import { qk } from '../lib/queryKeys';

/**
 * Discreet build-provenance footer (feature/version-footer). Fetches the public
 * GET /api/version and renders the short git SHA + image build time in low-emphasis
 * caption text at the bottom of the app shell. Build metadata, not chrome — quiet on
 * purpose. In local dev (both values null) or on any fetch error it renders a subdued
 * "local dev"; it never crashes the shell.
 */

const REPO_COMMIT_BASE = 'https://github.com/timeboxed-tech/ehe-agents/commit/';

/** Format an ISO-8601 UTC timestamp compactly as `2026-07-17 17:42 UTC`. Returns the
 * raw string if it isn't a parseable date, so a surprising value still shows something. */
function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  );
}

export function VersionFooter() {
  const version = useQuery({
    queryKey: qk.version(),
    queryFn: () => api.getVersion(),
    // Build provenance is fixed for the life of the process — fetch once, never refetch.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const gitSha = version.data?.gitSha ?? null;
  const buildTime = version.data?.buildTime ?? null;
  const isLocalDev = !gitSha && !buildTime;

  return (
    <Text
      as="footer"
      data-testid="version-footer"
      tone="secondary"
      size="xs"
      align="center"
      style={{
        padding: '0.5rem 1rem',
        marginTop: 'auto',
        borderTop: '1px solid var(--ehe-colors-border-subtle, rgba(120,120,120,0.25))',
        letterSpacing: '0.01em',
      }}
    >
      {isLocalDev ? (
        'local dev'
      ) : (
        <>
          {gitSha && (
            <>
              {'v '}
              <Link
                href={`${REPO_COMMIT_BASE}${gitSha}`}
                external
                muted
                style={{ fontFamily: 'var(--ehe-fonts-mono, ui-monospace, monospace)' }}
              >
                {gitSha}
              </Link>
            </>
          )}
          {gitSha && buildTime && ' · '}
          {buildTime && `built ${formatBuildTime(buildTime)}`}
        </>
      )}
    </Text>
  );
}
